import { ChevronDown, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { scrollToSection, artAssets } from "../data/site";
import { fetchMe } from "../utils/auth";
import { OrderButton } from "./OrderButton";

export function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountName, setAccountName] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchMe().then((u) => setAccountName(u?.name || null));
    const onAuth = (e) => setAccountName(e.detail.user?.name || null);
    window.addEventListener("dolo-auth", onAuth);
    return () => window.removeEventListener("dolo-auth", onAuth);
  }, []);

  const goToSection = (id) => {
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
      return;
    }
    scrollToSection(id);
  };

  return (
    <header className="site-header" data-testid="site-header">
      <Link to="/" className="brand-mark" data-testid="brand-home-link" onClick={() => setMenuOpen(false)}>
        <span className="brand-logo-wrap">
          <img src={artAssets.logo} alt="Dolo logo" className="brand-logo-image" />
          <img src={artAssets.logo} alt="" aria-hidden="true" className="brand-logo-image brand-logo-glitch glitch-red" />
          <img src={artAssets.logo} alt="" aria-hidden="true" className="brand-logo-image brand-logo-glitch glitch-cyan" />
        </span>
      </Link>

      <nav className="desktop-nav" aria-label="Primary navigation" data-testid="desktop-navigation">
        <button type="button" className="nav-link" onClick={() => goToSection("top")} data-testid="nav-top-button">
          HOME
        </button>
        <div className="nav-dropdown" data-testid="nav-manga-dropdown">
          <button type="button" className="nav-link" onClick={() => goToSection("manga")} data-testid="nav-manga-button">
            THE MANGA (VOL. 1) <ChevronDown size={12} className="nav-dropdown-caret" />
          </button>
          <div className="nav-dropdown-menu">
            <button type="button" onClick={() => goToSection("tcg")} data-testid="nav-tcg-button">
              WASTELAND TCG
            </button>
            <button type="button" onClick={() => goToSection("lore")} data-testid="nav-lore-button">
              LORE & ARCHIVES
            </button>
            <Link to="/sponsors" data-testid="nav-sponsors-link">
              SPONSORS
            </Link>
          </div>
        </div>
        <div className="nav-dropdown" data-testid="nav-game-dropdown">
          <Link
            to="/wasteland-run"
            className={`nav-link ${location.pathname === "/wasteland-run" || location.pathname === "/side-games" ? "is-active" : ""}`}
            data-testid="nav-game-link"
          >
            PLAY <span className="nav-new-badge" data-testid="nav-game-new-badge">NEW</span> <ChevronDown size={12} className="nav-dropdown-caret" />
          </Link>
          <div className="nav-dropdown-menu">
            <Link to="/side-games#dolos-night" data-testid="nav-dolos-night-link">
              DOLO'S NIGHT — MAZE
            </Link>
            <Link to="/side-games#dolosway" data-testid="nav-dolosway-link">
              DOLOSWAY — BOARD
            </Link>
            <Link to="/champions" data-testid="nav-champions-link">
              CHAMPIONS HALL
            </Link>
          </div>
        </div>
        <OrderButton className="nav-link nav-store" testId="nav-store-button">
          STORE
        </OrderButton>
        <Link
          to="/character-book"
          className={`nav-link ${location.pathname === "/character-book" ? "is-active" : ""}`}
          data-testid="nav-character-book-link"
        >
          CHARACTER BOOK
        </Link>
        <Link
          to="/riotball"
          className={`nav-link ${location.pathname === "/riotball" ? "is-active" : ""}`}
          data-testid="nav-riotball-link"
        >
          AL RIOTBALL
        </Link>
        <Link
          to="/wasteland-radio"
          className={`nav-link nav-radio ${location.pathname === "/wasteland-radio" ? "is-active" : ""}`}
          data-testid="nav-radio-link"
        >
          RADIO
        </Link>
        <div className="nav-dropdown nav-dolos-dropdown" data-testid="nav-dolos-dropdown">
          <Link
            to="/dolopals#dolo-bingo"
            className={`nav-link ${location.pathname === "/dolopals" ? "is-active" : ""}`}
            data-testid="nav-dolos-bingo-link"
          >
            DOLOS BINGO <ChevronDown size={12} className="nav-dropdown-caret" />
          </Link>
          <div className="nav-dropdown-menu">
            <Link to="/dolopals#adopt-a-tiny-titan" data-testid="nav-dolos-pals-link">
              DOLOS PALS
            </Link>
          </div>
        </div>
        <Link
          to="/rewards"
          className={`nav-link nav-rewards ${location.pathname === "/rewards" ? "is-active" : ""}`}
          data-testid="nav-rewards-link"
        >
          REWARDS
        </Link>
        <Link
          to="/account"
          className={`nav-link nav-account ${location.pathname === "/account" ? "is-active" : ""}`}
          data-testid="nav-account-link"
        >
          {accountName ? accountName.toUpperCase() : "ACCOUNT"}
        </Link>
      </nav>

      <div className="header-actions">
        <button
          type="button"
          className="header-read"
          onClick={() => goToSection("preview")}
          data-testid="header-read-chapter-button"
        >
          [ READ CHAPTER 1 ]
        </button>
        <button
          type="button"
          className="mobile-menu-button"
          onClick={() => setMenuOpen((value) => !value)}
          data-testid="mobile-menu-toggle-button"
          aria-label={menuOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {menuOpen ? (
        <div className="mobile-nav" data-testid="mobile-navigation">
          <button type="button" onClick={() => goToSection("top")} data-testid="mobile-nav-top-button">
            HOME
          </button>
          <button type="button" onClick={() => goToSection("manga")} data-testid="mobile-nav-manga-button">
            THE MANGA (VOL. 1)
          </button>
          <button type="button" className="mobile-nav-sub" onClick={() => goToSection("tcg")} data-testid="mobile-nav-tcg-button">
            — WASTELAND TCG
          </button>
          <button type="button" className="mobile-nav-sub" onClick={() => goToSection("lore")} data-testid="mobile-nav-lore-button">
            — LORE & ARCHIVES
          </button>
          <Link to="/sponsors" onClick={() => setMenuOpen(false)} className="mobile-nav-sub" data-testid="mobile-nav-sponsors-link">
            — SPONSORS
          </Link>
          <Link to="/wasteland-run" onClick={() => setMenuOpen(false)} data-testid="mobile-game-link">
            PLAY <span className="nav-new-badge">NEW</span>
          </Link>
          <Link to="/side-games#dolos-night" onClick={() => setMenuOpen(false)} className="mobile-nav-sub" data-testid="mobile-nav-dolos-night-link">
            — DOLO'S NIGHT MAZE
          </Link>
          <Link to="/side-games#dolosway" onClick={() => setMenuOpen(false)} className="mobile-nav-sub" data-testid="mobile-nav-dolosway-link">
            — DOLOSWAY BOARD
          </Link>
          <Link to="/champions" onClick={() => setMenuOpen(false)} className="mobile-nav-sub" data-testid="mobile-nav-champions-link">
            — CHAMPIONS HALL
          </Link>
          <OrderButton className="mobile-nav-store" testId="mobile-nav-store-button">
            STORE
          </OrderButton>
          <Link to="/character-book" onClick={() => setMenuOpen(false)} data-testid="mobile-character-book-link">
            CHARACTER BOOK
          </Link>
          <Link to="/riotball" onClick={() => setMenuOpen(false)} data-testid="mobile-riotball-link">
            AL RIOTBALL
          </Link>
          <Link to="/wasteland-radio" onClick={() => setMenuOpen(false)} data-testid="mobile-radio-link" className="nav-radio">
            RADIO
          </Link>
          <Link to="/dolopals#dolo-bingo" onClick={() => setMenuOpen(false)} data-testid="mobile-dolos-bingo-link">
            DOLOS BINGO
          </Link>
          <Link to="/dolopals#adopt-a-tiny-titan" onClick={() => setMenuOpen(false)} data-testid="mobile-dolos-pals-link" className="mobile-nav-sub">
            — DOLOS PALS
          </Link>
          <Link to="/rewards" onClick={() => setMenuOpen(false)} data-testid="mobile-rewards-link" className="nav-rewards">
            REWARDS
          </Link>
          <Link to="/account" onClick={() => setMenuOpen(false)} data-testid="mobile-account-link" className="nav-account">
            {accountName ? accountName.toUpperCase() : "ACCOUNT"}
          </Link>
        </div>
      ) : null}
    </header>
  );
}
